// Pure display formatters for the flyover-videos list. Kept out of the components so they're
// unit-testable (see __tests__/formatters.test.ts).

export const ORIENTATION_ALL = 'ALL';
export const ORIENTATION_VERTICAL = 'PORTRAIT_9_16';
export const ORIENTATION_HORIZONTAL = 'LANDSCAPE_16_9';

/** True when the stored aspect (or GraphQL enum) is the vertical 9:16 orientation. */
export function isVertical(aspect?: string | null): boolean {
  return aspect === ORIENTATION_VERTICAL || aspect === '9x16';
}

/** Short orientation label matching the app: "Vertical" / "Landscape". */
export function orientationLabel(aspect?: string | null): string {
  return isVertical(aspect) ? 'Vertical' : 'Landscape';
}

/**
 * Material-icon name for an orientation — outline glyphs (an outlined portrait phone for vertical,
 * an outlined landscape rectangle for landscape), never a solid fill.
 */
export function orientationIcon(aspect?: string | null): string {
  return isVertical(aspect) ? 'stay_current_portrait' : 'crop_landscape';
}

/** Bytes → "12.3 MB" / "512 KB". Blank when unknown (older renders report null). */
export function formatFileSize(bytes?: number | null): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/** Seconds → "m:ss" (e.g. 15 → "0:15", 90 → "1:30"). Blank when unknown. */
export function formatDuration(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '';
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * ISO date → a relative phrase matching the app ("Last week", "2 weeks ago", "Last month", …).
 * `now` is injectable so the output is deterministic in tests. Blank when unparseable.
 */
export function formatRelativeDate(iso?: string | null, now: Date = new Date()): string {
  if (!iso) return '';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';

  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'Last week';
  if (days < 28) return `${Math.floor(days / 7)} weeks ago`;
  if (days < 60) return 'Last month';
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  const years = Math.floor(days / 365);
  return years === 1 ? 'Last year' : `${years} years ago`;
}

export interface CardSubject {
  renderKind?: string | null;
  recordingTitle?: string | null;
  recordingObfuscatedSlug?: string | null;
  hikeTitle?: string | null;
  hikeUrl?: string | null;
}

/** True for a Trail Trailer row (a hike's video) rather than a recording flyover. */
export function isTrailerVideo(video: CardSubject | null | undefined): boolean {
  return video?.renderKind === 'trailer';
}

/** Card title: the recording's name, the hike's name for a trailer, or a kind-appropriate fallback. */
export function videoCardTitle(video: CardSubject | null | undefined): string {
  if (isTrailerVideo(video)) return video?.hikeTitle || 'Untitled hike';
  return video?.recordingTitle || 'Untitled recording';
}

/**
 * The card's "view the subject" link — the recording's page for a flyover, the hike's page for a
 * trailer (absolute URL from the server, because official and user hikes live at different paths).
 * Null when the row carries no usable slug/url, so the card simply omits the link.
 */
export function videoCardLink(video: CardSubject | null | undefined): { url: string; label: string } | null {
  if (isTrailerVideo(video)) {
    return video?.hikeUrl ? { url: video.hikeUrl, label: 'View hike' } : null;
  }
  return video?.recordingObfuscatedSlug
    ? { url: `/recordings/users/${video.recordingObfuscatedSlug}`, label: 'View recording' }
    : null;
}

/** What the share wizard's link targets should carry for this row: the subject's page. */
export function videoShareUrl(video: CardSubject | null | undefined): string {
  return videoCardLink(video)?.url || '';
}
